import { useCallback, useEffect } from 'react'
import { Box, Button, CircularProgress, Typography, Checkbox as MuiCheckbox, FormControlLabel, useMediaQuery } from '@mui/material'
import { StepTitle } from '../StepTitle'
import { useForm, FormProvider, useWatch, Controller } from 'react-hook-form'
import { Radio } from '~/components/Form/Radio'
import { Input } from '~/components/Form/Input'
import { Checkbox } from '~/components/Form/Checkbox'
import { IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react'
import { ENotificationRecipient, ContractGetQuery, useNotifyPartnerFinishedDocumentationMutation, useCustomerGetQuery } from '~/graphql/types'
import { InputPendenciesStatus } from '../InputPendenciesStatus'
import { FieldsType } from '../types'
import { Dialog } from '~/components'
import { useToggle } from '~/hooks'
import { toast } from 'react-toastify'
import { theme } from '~/utils'
import { useAuth } from '~/contexts'

type BasicDataProps = {
  contract: ContractGetQuery['contractGet']
  onRefuse: Function
  onApprove: Function
  modalType: 'approve' | 'refuse'
  setFields: React.Dispatch<React.SetStateAction<FieldsType>>
}

type NotificationRecipientData = {
  recipients: ENotificationRecipient[]
}

type BasicDataForm = {
  personType: string,
  name: string,
  document: string,
  email: string,
  phone: string,
  uf: string,
  pendencies: {
    personType: boolean,
    name: boolean,
    email: boolean,
    phone: boolean,
    uf: boolean,
  }
  controlPendencies: boolean
}

export const BasicData: React.FC<BasicDataProps> = ({ contract, onRefuse, onApprove, setFields }) => {
  // const isPartnerContract = Boolean(contract?.indicationRef?.partner?.name)
  const modalFormMethods = useForm<NotificationRecipientData>()
  const formMethods = useForm<BasicDataForm>()
  const notificationModal = useToggle(false)
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))

  const formModalWatch = useWatch({ control: modalFormMethods.control })

  const [notifyCustomer, { loading: notifyIsLoading }] = useNotifyPartnerFinishedDocumentationMutation({
    onCompleted: () => {
      toast.success(`O parceiro ${contract?.indicationRef?.partner?.name} foi notificado.`)
      notificationModal.setIsTrue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      notificationModal.setIsTrue(false)
    }
  })

  const { authUser } = useAuth()
  const isSeller = authUser?.user.type === 'seller'

  const { data: customerData } = useCustomerGetQuery({
    variables: {
      id: contract?.customerId || ''
    }
  })

  useEffect(() => {
    if (contract?.customerRef && customerData?.customerGet) {
      formMethods.setValue('personType', contract.customerRef.personType)
      formMethods.setValue('name', contract.customerRef.name)
      formMethods.setValue('email', contract.customerRef.email || '')
      formMethods.setValue('phone', contract.customerRef.phone || '')
      formMethods.setValue('uf', customerData?.customerGet?.uf || '')
    }
    // if (contract?.indicationRef?.partner?.name) {
    //   modalFormMethods.setValue('recipients', [ENotificationRecipient.customer, ENotificationRecipient.partner])
    // } else {
    //   modalFormMethods.setValue('recipients', [ENotificationRecipient.customer])
    // }
  }, [contract, customerData])

  function handleSubmit(data: BasicDataForm) {
    const selectedFields = Object?.entries(data?.pendencies).filter(item => item[1])
    const objectFields = Object.fromEntries(selectedFields)
    setFields(objectFields)
  }

  const { pendencies } = useWatch({
    control: formMethods.control,
  })

  function onSelectAllFields(isValid: boolean) {
    formMethods.setValue('controlPendencies', !isValid)
    formMethods.setValue('pendencies.name', !isValid)
    formMethods.setValue('pendencies.email', !isValid)
    formMethods.setValue('pendencies.phone', !isValid)
    formMethods.setValue('pendencies.uf', !isValid)
  }

  const enableSelectAll = [
    Boolean(!contract?.customerRef?.name),
    Boolean(!contract?.customerRef?.email),
    Boolean(!contract?.customerRef?.phone),
    Boolean(!customerData?.customerGet?.uf),
  ].some(item => item)

  const buttonIsEnable = pendencies && Object?.values(pendencies)?.some(item => item)

  useEffect(() => {
    if (contract) {
      formMethods.setValue('pendencies', {
        name: false,
        email: false,
        personType: false,
        phone: false,
        uf: false
      })
    }
  }, [contract])

  const onNotify = useCallback(() => {
    notifyCustomer({ variables: { params: { contractId: contract?._id || '' } } })
  }, [contract])

  return (
    <>
      <StepTitle contractId={contract?._id} clientName={contract?.customerRef.name} step={{ value: '1', title: 'Dados básicos' }} />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}>

          <Typography sx={{ padding: '1rem 0 0 0' }} variant='h6'>Tipo de pessoa:</Typography>
          <Radio radioProps={{ disabled: true }} disabled row options={[{ value: 'pf', label: 'Pessoa física' }, { value: 'pj', label: 'Pessoa Jurídica' }]} name='personType' />

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '1rem 0 1rem 0', justifyContent: 'space-between' }}>

            <Typography sx={{ padding: '1rem 0 0 0' }} variant='h6'>Dados pessoais</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              {!isLowerSm &&
                <Typography fontWeight={500} color={theme.palette.grey[600]} fontSize='.95rem' variant='h6'>Selecionar todos</Typography>
              }
              <Controller
                name='controlPendencies'
                control={formMethods.control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    {...field}
                    sx={{ margin: 0 }}
                    onChange={(e: any) => {
                      field.onChange(e)
                      onSelectAllFields(field.value)
                    }}
                    control={<MuiCheckbox disabled={enableSelectAll} />}
                    label={<Typography variant='body2' color='grey.400'></Typography>}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '1rem 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.name} /> } }}
              disabled
              name='name'
              label='Nome completo'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.customerRef?.name) }} name='pendencies.name' />
          </Box>

          {/* <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.document} /> } }}
              mask='cpfCnpj'
              disabled
              name='document'
              label='CPF/CNPJ'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.customerRef?.document) }} name='pendencies.document' />
          </Box> */}

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.email} /> } }}
              disabled
              name='email'
              label='E-mail'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.customerRef?.email) }} name='pendencies.email' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.phone} /> } }}
              mask='phone'
              disabled
              name='phone'
              label='Telefone'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.customerRef?.phone) }} name='pendencies.phone' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.uf} /> } }}
              disabled
              name='uf'
              label='UF'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!customerData?.customerGet?.uf) }} name='pendencies.uf' />
          </Box>

          {/* <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.dealershipName} /> } }}
              disabled
              name='dealership'
              label='Fornecedora de energia'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.energyBillInfo?.dealershipName) }} name='pendencies.dealershipName' />
          </Box> */}

          {/* <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              mask='BRL'
              icons={{ end: { element: <InputPendenciesStatus pendencie={contract?.customerRef?.pendencies?.averageEnergyBillValue} /> } }}
              disabled
              name='averageEnergyBillValue'
              label='Valor médio de consumo'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!contract?.energyBillInfo?.avarageBillAmountCents) }} name='pendencies.averageEnergyBillValue' />
          </Box> */}

          <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', flexFlow: isLowerMd ? 'column' : 'row' }}>
            <Box sx={{ display: 'flex', gap: isLowerSm ? '0' : '1rem', justifyContent: 'space-between', width: '100%' }}>
              <Button
                size='small'
                disabled={isSeller}
                type='button'
                onClick={() => notificationModal.toggle()}
                startIcon={isLowerSm ? <></> : <IconInfoCircle />}
                color='secondary'
              >
                Enviar notificação
              </Button>
              <Button size='small' disabled={!buttonIsEnable || isSeller} type='submit' onClick={() => onRefuse()} startIcon={<IconX />} color='secondary'>
                Recusar {isLowerSm ? '' : 'selecionados'}
              </Button>
            </Box>
            <Box sx={{ minWidth: isLowerMd ? '' : '230px' }}>
              <Button fullWidth={isLowerMd} disabled={!buttonIsEnable || isSeller} type='submit' onClick={() => onApprove()} startIcon={<IconCheck />}>Aprovar selecionados</Button>
            </Box>
          </Box>
        </form>
      </FormProvider>

      <Dialog onClose={() => notificationModal.toggle()} isVisible={notificationModal.isTrue}>
        <FormProvider {...modalFormMethods}>
          <form onSubmit={modalFormMethods.handleSubmit(() => onNotify())}>
            <Box sx={{ width: '100%' }}>
              {notifyIsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                  <CircularProgress size={140} color='inherit' />
                </Box>

              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '.8rem', paddingBottom: '1.4rem' }}>
                    <Typography variant='h6'>Enviar notificação</Typography>
                  </Box>

                  <Box sx={{ padding: '1rem 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>

                    <Typography>
                      A notificação informará que os dados e documentos foram analisados, porém existem <b>dados inconsistentes ou rejeitados</b> que precisam ser revistos
                    </Typography>
                    {/* <CheckboxMultiple name='recipients' options={notificationRecipientsFilterOptions} /> */}

                    <Typography sx={{ paddingTop: '.8rem' }}>Deseja notificar o parceiro?</Typography>

                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: isLowerSm ? 'auto' : 'center',
                    justifyContent: isLowerSm ? 'auto' : 'flex-end',
                    gap: '1rem', paddingTop: '1.4rem',
                    flexFlow: isLowerSm ? 'column' : 'row'
                  }}>
                    <Button type='button' onClick={() => notificationModal.toggle()} color='secondary'>Cancelar</Button>
                    <Button disabled={formModalWatch?.recipients && !(formModalWatch?.recipients?.length > 0)} type='submit'>Sim, notificar agora</Button>
                  </Box>
                </>
              )}

            </Box>
          </form>
        </FormProvider>
      </Dialog>

    </>
  )
}
